/* A&Y — Forêt douce · script principal (en-tête, menu, recherche, panier, révélation, confort) */
(() => {
  'use strict';
  const root = document.documentElement;
  const strings = (() => { try { return JSON.parse(document.getElementById('ay-strings')?.textContent || '{}'); } catch (_) { return {}; } })();
  const routes = window.ayRoutes || { root: '/', cart: '/cart', search: '/search', country: 'CA', currency: 'CAD', locale: 'fr' };
  const rootPath = routes.root.replace(/\/$/, '');
  const en = routes.locale === 'en';
  const t = (key, vars = {}) => String(strings[key] || key).replace(/\{(\w+)\}/g, (m, k) => (vars[k] ?? m));
  const money = cents => window.ayMoney ? window.ayMoney(cents) : new Intl.NumberFormat(en ? 'en-CA' : 'fr-CA', { style: 'currency', currency: routes.currency || 'CAD' }).format(cents / 100);
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const isReduced = () => reduced.matches || root.dataset.ayReduced === 'true';
  const imageUrl = (url, width) => { if (!url) return ''; const clean = url.replace(/^\/\//, 'https://'); return clean + (clean.includes('?') ? '&' : '?') + 'width=' + width; };
  const el = (tag, attrs = {}, children = []) => {
    const node = document.createElement(tag);
    for (const [k, v] of Object.entries(attrs)) { if (v === false || v == null) continue; if (k === 'text') node.textContent = v; else if (k === 'html') node.innerHTML = v; else node.setAttribute(k, v === true ? '' : v); }
    for (const child of [].concat(children)) if (child) node.append(child);
    return node;
  };

  /* ------------------------------------------------------------------ */
  /* 1. En-tête : hauteur, état défilé                                   */
  /* ------------------------------------------------------------------ */
  const header = document.querySelector('[data-ay-header]');
  function measureHeader() {
    if (!header) return;
    root.style.setProperty('--ay-header-offset', header.offsetHeight + 'px');
  }
  if (header) {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => { header.classList.toggle('is-scrolled', window.scrollY > 12); ticking = false; });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    if ('ResizeObserver' in window) new ResizeObserver(measureHeader).observe(header); else window.addEventListener('resize', measureHeader);
    measureHeader();
  }

  /* ------------------------------------------------------------------ */
  /* 2. Menu plein écran                                                 */
  /* ------------------------------------------------------------------ */
  const menu = document.querySelector('[data-ay-menu]');
  const burger = document.querySelector('[data-ay-menu-open]');
  let lastFocus = null;
  function openMenu() {
    if (!menu) return;
    lastFocus = document.activeElement;
    menu.dataset.open = 'true';
    menu.removeAttribute('aria-hidden');
    burger?.setAttribute('aria-expanded', 'true');
    root.classList.add('ay-menu-open');
    const first = menu.querySelector('a, button, summary');
    window.setTimeout(() => first?.focus({ preventScroll: true }), isReduced() ? 0 : 320);
  }
  function closeMenu() {
    if (!menu || menu.dataset.open !== 'true') return;
    menu.dataset.open = 'false';
    menu.setAttribute('aria-hidden', 'true');
    burger?.setAttribute('aria-expanded', 'false');
    root.classList.remove('ay-menu-open');
    (lastFocus || burger)?.focus?.({ preventScroll: true });
  }
  burger?.addEventListener('click', () => { menu?.dataset.open === 'true' ? closeMenu() : openMenu(); });
  menu?.querySelectorAll('[data-ay-menu-close]').forEach(button => button.addEventListener('click', closeMenu));
  menu?.addEventListener('click', event => { if (event.target.closest('a[href]')) closeMenu(); });
  menu?.addEventListener('keydown', event => {
    if (event.key === 'Escape') { event.preventDefault(); closeMenu(); return; }
    if (event.key !== 'Tab') return;
    const focusables = [...menu.querySelectorAll('a[href], button:not([disabled]), summary, select, input')].filter(node => node.offsetParent !== null);
    if (!focusables.length) return;
    const first = focusables[0], last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  });
  window.matchMedia('(min-width: 1100px)').addEventListener('change', event => { if (event.matches) closeMenu(); });

  /* Méga-menu et sélecteurs <details> : fermeture à l'extérieur / Échap */
  const closeOnOutside = selector => {
    document.addEventListener('click', event => {
      document.querySelectorAll(selector).forEach(details => { if (details.open && !details.contains(event.target)) details.open = false; });
    });
    document.addEventListener('keydown', event => {
      if (event.key !== 'Escape') return;
      document.querySelectorAll(selector).forEach(details => { if (details.open && details.contains(document.activeElement)) { details.open = false; details.querySelector('summary')?.focus(); } });
    });
  };
  closeOnOutside('.ay-nav .ay-shop-menu, .ay-market-switcher, [data-ay-comfort]');
  document.querySelectorAll('.ay-nav .ay-shop-menu').forEach(details => {
    details.addEventListener('toggle', () => { if (details.open) document.querySelectorAll('.ay-nav .ay-shop-menu').forEach(other => { if (other !== details) other.open = false; }); });
  });

  /* ------------------------------------------------------------------ */
  /* 3. Recherche prédictive                                             */
  /* ------------------------------------------------------------------ */
  const search = document.getElementById('AYSearch');
  if (search && typeof search.showModal === 'function') {
    const input = search.querySelector('[data-ay-search-input]');
    const results = search.querySelector('[data-ay-search-results]');
    const hint = () => { results.replaceChildren(el('p', { class: 'ay-search__hint', text: t('searchHint') })); };
    let timer = 0, controller = null, lastTerms = '';
    const render = (terms, data) => {
      const resources = data?.resources?.results || {};
      const groups = [['searchProducts', resources.products || []], ['searchArticles', resources.articles || []], ['searchPages', resources.pages || []]];
      const fragment = document.createDocumentFragment();
      let total = 0;
      for (const [label, items] of groups) {
        if (!items.length) continue;
        total += items.length;
        const group = el('div', { class: 'ay-search__group' }, el('p', { class: 'ay-search__group-title', text: t(label) }));
        for (const item of items) {
          const isProduct = label === 'searchProducts';
          const link = el('a', { class: 'ay-search__item' + (isProduct ? '' : ' ay-search__item--text'), href: item.url });
          if (isProduct) {
            const image = item.featured_image?.url || item.image;
            link.append(image ? el('img', { src: imageUrl(image, 160), alt: '', loading: 'lazy', width: 56, height: 70 }) : el('span'));
            link.append(el('span', {}, [el('strong', { text: item.title }), item.vendor ? el('small', { text: item.vendor }) : null]));
            link.append(el('span', { class: 'ay-price', text: item.price ? money(Math.round(parseFloat(item.price) * 100)) : '' }));
          } else {
            link.append(el('span', {}, [el('strong', { text: item.title }), item.summary_html || item.body ? el('small', { text: String(item.summary_html || item.body).replace(/<[^>]+>/g, '').slice(0, 110) }) : null]));
          }
          group.append(link);
        }
        fragment.append(group);
      }
      if (!total) fragment.append(el('p', { class: 'ay-search__hint', text: t('searchNone', { terms }) }));
      fragment.append(el('a', { class: 'ay-search__all', href: routes.search + '?q=' + encodeURIComponent(terms), text: t('searchAll', { terms }) }));
      results.replaceChildren(fragment);
    };
    const run = async () => {
      const terms = input.value.trim();
      if (terms.length < 2) { hint(); return; }
      if (terms === lastTerms) return;
      lastTerms = terms;
      controller?.abort();
      controller = new AbortController();
      try {
        const url = rootPath + '/search/suggest.json?q=' + encodeURIComponent(terms) + '&resources[type]=product,article,page&resources[limit]=6&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,variants.title,vendor,tag';
        const response = await fetch(url, { signal: controller.signal, headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('search');
        render(terms, await response.json());
      } catch (error) { if (error.name !== 'AbortError') results.replaceChildren(el('a', { class: 'ay-search__all', href: routes.search + '?q=' + encodeURIComponent(terms), text: t('searchAll', { terms }) })); }
    };
    input.addEventListener('input', () => { window.clearTimeout(timer); timer = window.setTimeout(run, 220); });
    document.querySelectorAll('[data-ay-search-open]').forEach(trigger => trigger.addEventListener('click', event => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      closeMenu();
      if (!search.open) search.showModal();
      hint();
      window.setTimeout(() => input.focus(), 50);
    }));
    search.querySelector('[data-ay-search-close]')?.addEventListener('click', () => search.close());
    search.addEventListener('click', event => { if (event.target === search) search.close(); });
    document.addEventListener('keydown', event => { if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') { event.preventDefault(); search.open ? search.close() : (search.showModal(), hint(), input.focus()); } });
  }

  /* ------------------------------------------------------------------ */
  /* 4. Tiroir panier + ajout AJAX                                       */
  /* ------------------------------------------------------------------ */
  const drawer = document.getElementById('AYCartDrawer');
  const cartApi = {
    async get() { const r = await fetch(rootPath + '/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store', credentials: 'same-origin' }); if (!r.ok) throw new Error('cart'); return r.json(); },
    async change(line, quantity) { const r = await fetch(rootPath + '/cart/change.js', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ line, quantity }) }); if (!r.ok) throw new Error('change'); return r.json(); },
    async add(body) { const r = await fetch(rootPath + '/cart/add.js', { method: 'POST', credentials: 'same-origin', headers: { Accept: 'application/json' }, body }); const data = await r.json().catch(() => ({})); if (!r.ok) throw new Error(data.description || data.message || t('cartAddError')); return data; }
  };
  function updateCount(count) {
    document.querySelectorAll('[data-ay-cart-count]').forEach(node => {
      node.textContent = count;
      node.dataset.count = count;
      node.classList.add('is-bump');
      window.setTimeout(() => node.classList.remove('is-bump'), 360);
    });
    document.querySelectorAll('[data-ay-cart-link]').forEach(link => {
      const base = link.getAttribute('data-ay-cart-label') || '';
      if (base) link.setAttribute('aria-label', base + ', ' + count + ' ' + (count === 1 ? t('item') : t('items')));
    });
  }
  function renderCart(cart) {
    if (!drawer) return;
    const items = drawer.querySelector('[data-ay-cart-items]');
    const status = drawer.querySelector('[data-ay-cart-status]');
    const shipping = drawer.querySelector('[data-ay-shipping]');
    const countNode = drawer.querySelector('[data-ay-drawer-count]');
    const empty = drawer.querySelector('[data-ay-cart-empty]');
    const foot = drawer.querySelector('[data-ay-cart-foot]');
    if (countNode) countNode.textContent = cart.item_count ? cart.item_count + ' ' + (cart.item_count === 1 ? t('item') : t('items')) : '';
    status.textContent = '';
    items.replaceChildren();
    cart.items.forEach((item, index) => {
      const li = el('li', {});
      const photo = el('img', { alt: '', loading: 'lazy', width: 76, height: 96 });
      if (item.image) photo.src = imageUrl(item.image, 200);
      const body = el('div', {});
      body.append(el('a', { href: item.url, text: item.product_title }));
      if (item.variant_title && item.variant_title !== 'Default Title') body.append(el('p', { text: item.variant_title }));
      const properties = Object.entries(item.properties || {}).filter(([k, v]) => v && !k.startsWith('_'));
      if (properties.length) body.append(el('p', { text: properties.map(([k, v]) => k + ' : ' + v).join(' · ') }));
      const line = el('div', { class: 'ay-cart-drawer__line' });
      const qty = el('div', { class: 'ay-qty', role: 'group', 'aria-label': t('quantity') });
      const minus = el('button', { type: 'button', 'aria-label': t('decrease'), text: '−' });
      const value = el('output', { text: String(item.quantity) });
      const plus = el('button', { type: 'button', 'aria-label': t('increase'), text: '+' });
      qty.append(minus, value, plus);
      minus.addEventListener('click', () => changeLine(index + 1, item.quantity - 1));
      plus.addEventListener('click', () => changeLine(index + 1, item.quantity + 1));
      const price = el('span', { class: 'ay-cart-drawer__price' });
      if (item.final_line_price < item.original_line_price) {
        price.append(el('s', { text: money(item.original_line_price) }), ' ', el('strong', { text: money(item.final_line_price) }));
        const promoNames = (item.line_level_discount_allocations || []).map(d => d.discount_application && d.discount_application.title).filter(Boolean);
        if (promoNames.length) body.append(el('p', { class: 'ay-cart-drawer__promo', text: '✓ ' + [...new Set(promoNames)].join(', ') }));
      } else price.textContent = money(item.final_line_price);
      line.append(qty, price);
      body.append(line);
      const remove = el('button', { type: 'button', class: 'ay-cart-drawer__remove', text: t('remove') });
      remove.addEventListener('click', () => changeLine(index + 1, 0));
      body.append(remove);
      li.append(photo, body);
      items.append(li);
    });
    if (empty) empty.hidden = cart.item_count > 0;
    if (foot) foot.hidden = cart.item_count === 0;
    const discountLine = drawer.querySelector('[data-ay-cart-discount]');
    const orderDiscount = Math.max(0, cart.items.reduce((sum, item) => sum + item.final_line_price, 0) - cart.total_price);
    if (discountLine) { discountLine.hidden = !orderDiscount; discountLine.textContent = t('orderDiscounts') + ' : −' + money(orderDiscount); }
    const total = drawer.querySelector('[data-ay-cart-total]');
    if (total) total.replaceChildren(el('span', { text: t('subtotal') }), el('span', { text: money(cart.total_price) }));
    if (shipping) {
      const canada = routes.country === 'CA' && cart.currency === 'CAD' && cart.item_count > 0;
      shipping.hidden = !canada;
      if (canada) {
        const remaining = Math.max(0, 7500 - cart.total_price), remainingCanada = Math.max(0, 10000 - cart.total_price);
        shipping.querySelector('progress').value = Math.min(10000, cart.total_price);
        shipping.querySelector('[data-ay-shipping-message]').textContent = remaining ? t('shippingRemaining', { amount: money(remaining) }) : remainingCanada ? t('shippingQuebec', { amount: money(remainingCanada) }) : t('shippingCanada');
      }
    }
    updateCount(cart.item_count);
    document.dispatchEvent(new CustomEvent('ay:cart:updated', { detail: cart }));
    renderUpsell(cart);
    /* Suggestions « Complétez votre ensemble » (recommandations Shopify, 3 articles max) */
    async function renderUpsell(cart) {
      const box = drawer.querySelector('[data-ay-cart-upsell]');
      if (!box) return;
      if (!cart.item_count) { box.hidden = true; return; }
      const cache = drawer._ayUpsellCache = drawer._ayUpsellCache || {};
      const seed = cart.items[0].product_id;
      const inCart = new Set(cart.items.map(i => i.product_id));
      try {
        if (!cache[seed]) {
          const r = await fetch(rootPath + '/recommendations/products.json?product_id=' + seed + '&limit=8&intent=related', { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
          cache[seed] = r.ok ? ((await r.json()).products || []) : [];
        }
        const picks = cache[seed].filter(p => !inCart.has(p.id) && p.available && p.price <= 4500 && !/ZEM/i.test(p.title)).slice(0, 3);
        const list = box.querySelector('[data-ay-upsell-list]');
        if (!list) return;
        list.replaceChildren();
        if (!picks.length) { box.hidden = true; return; }
        picks.forEach(p => {
          const v = (p.variants || []).find(x => x.available) || (p.variants || [])[0];
          if (!v) return;
          const card = el('div', { class: 'ay-upsell__card' });
          const img = el('img', { alt: '', loading: 'lazy', width: 56, height: 70, src: imageUrl(p.featured_image, 160) });
          const title = String(p.title).split(/ [—–] /).sort((a, b) => b.length - a.length)[0].slice(0, 52);
          const info = el('div', {}, [el('a', { href: p.url, text: title }), el('span', { text: money(p.price) })]);
          const add = el('button', { type: 'button', class: 'ay-upsell__add', text: en ? '+ Add' : '+ Ajouter' });
          add.addEventListener('click', async () => {
            add.disabled = true;
            try { const fd = new FormData(); fd.append('id', v.id); fd.append('quantity', '1'); await cartApi.add(fd); await refreshCart(); }
            catch (_) { add.disabled = false; drawer.querySelector('[data-ay-cart-status]').textContent = t('cartAddError'); }
          });
          card.append(img, info, add);
          list.append(card);
        });
        box.hidden = false;
      } catch (_) { box.hidden = true; }
    }
  }
  async function refreshCart() {
    if (!drawer) return;
    const status = drawer.querySelector('[data-ay-cart-status]');
    drawer.setAttribute('aria-busy', 'true');
    try { renderCart(await cartApi.get()); }
    catch (_) { status.textContent = t('cartError'); }
    finally { drawer.removeAttribute('aria-busy'); }
  }
  async function changeLine(line, quantity) {
    if (!drawer) return;
    drawer.setAttribute('aria-busy', 'true');
    try { renderCart(await cartApi.change(line, Math.max(0, quantity))); }
    catch (_) { drawer.querySelector('[data-ay-cart-status]').textContent = t('cartError'); }
    finally { drawer.removeAttribute('aria-busy'); }
  }
  function openDrawer(message) {
    if (!drawer || typeof drawer.showModal !== 'function') { window.location.assign(routes.cart); return; }
    closeMenu();
    if (!drawer.open) drawer.showModal();
    const status = drawer.querySelector('[data-ay-cart-status]');
    if (message) status.textContent = message;
    refreshCart().then(() => { if (message) status.textContent = message; });
  }
  if (drawer && typeof drawer.showModal === 'function') {
    /* Blocs ajoutés par script : suggestions (sous les articles) et rappel de confiance (sous le bouton) */
    if (!drawer.querySelector('[data-ay-cart-upsell]')) {
      const upsell = el('div', { class: 'ay-upsell', 'data-ay-cart-upsell': true, hidden: true }, [el('p', { class: 'ay-upsell__title', text: en ? 'Complete the set' : 'Complétez votre ensemble' }), el('div', { class: 'ay-upsell__list', 'data-ay-upsell-list': true })]);
      const itemsList = drawer.querySelector('[data-ay-cart-items]');
      if (itemsList) itemsList.insertAdjacentElement('afterend', upsell);
    }
    const cta = drawer.querySelector('[data-ay-cart-cta]');
    if (cta && !drawer.querySelector('.ay-cart-drawer__trust')) cta.insertAdjacentElement('afterend', el('p', { class: 'ay-cart-drawer__trust', text: en ? 'Secure checkout · Prepared in Montréal · Here to help' : 'Paiement sécurisé · Préparé à Montréal · Service à l’écoute' }));
    drawer.querySelectorAll('[data-ay-cart-close]').forEach(button => button.addEventListener('click', () => drawer.close()));
    drawer.addEventListener('click', event => { if (event.target === drawer) drawer.close(); });
    document.addEventListener('click', event => {
      const link = event.target.closest('[data-ay-cart-link]');
      if (!link || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0) return;
      event.preventDefault();
      openDrawer();
    });
    document.addEventListener('ay:cart:open', event => openDrawer(event.detail?.message));
    /* Ajout AJAX : formulaires marqués data-ay-ajax-cart */
    document.addEventListener('submit', async event => {
      const form = event.target.closest('form[data-ay-ajax-cart]');
      if (!form || form.dataset.ayBusy === 'true') return;
      event.preventDefault();
      const button = form.querySelector('[type="submit"], [data-add-button]');
      const original = button?.textContent;
      form.dataset.ayBusy = 'true';
      button?.setAttribute('aria-busy', 'true');
      try {
        const body = new FormData(form);
        body.delete('options[]');
        await cartApi.add(body);
        openDrawer(t('cartAdded'));
        document.dispatchEvent(new CustomEvent('ay:cart:added', { detail: { form } }));
      } catch (error) {
        const status = form.querySelector('[data-ay-form-status]');
        if (status) status.textContent = error.message || t('cartAddError'); else window.alert(error.message || t('cartAddError'));
      } finally {
        form.dataset.ayBusy = 'false';
        button?.removeAttribute('aria-busy');
        if (button && original) button.textContent = original;
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /* 5. Révélation au défilement                                         */
  /* ------------------------------------------------------------------ */
  const reveal = (() => {
    if (!('IntersectionObserver' in window)) { document.querySelectorAll('main .shopify-section').forEach(section => section.classList.add('is-in')); return null; }
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => { if (entry.isIntersecting) { entry.target.classList.add('is-in'); observer.unobserve(entry.target); } });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.02 });
    const observe = scope => scope.querySelectorAll('main .shopify-section').forEach(section => {
      if (section.classList.contains('is-in')) return;
      const rect = section.getBoundingClientRect();
      if (rect.top < window.innerHeight * 0.9) { section.classList.add('is-in'); return; }
      observer.observe(section);
    });
    observe(document);
    return observe;
  })();
  document.addEventListener('shopify:section:load', event => { event.target.classList.add('is-in'); reveal?.(document); measureHeader(); });
  document.addEventListener('shopify:section:select', event => { event.target.classList.add('is-in'); });

  /* ------------------------------------------------------------------ */
  /* 6. Interactions génériques : tri, filtres, quantités                */
  /* ------------------------------------------------------------------ */
  document.querySelectorAll('.ay-sort-form select').forEach(select => select.addEventListener('change', () => select.form?.requestSubmit?.() ?? select.form?.submit()));
  const filters = document.getElementById('AYFilters');
  if (filters) {
    document.querySelectorAll('[data-ay-filters-open]').forEach(button => button.addEventListener('click', () => { if (typeof filters.showModal === 'function' && !filters.open) filters.showModal(); }));
    filters.querySelectorAll('[data-ay-filters-close]').forEach(button => button.addEventListener('click', () => filters.close()));
    filters.addEventListener('click', event => { if (event.target === filters) filters.close(); });
    const form = filters.querySelector('form');
    const desktop = window.matchMedia('(min-width: 1000px)');
    let filterTimer = 0;
    form?.addEventListener('change', event => {
      if (!desktop.matches || event.target.type === 'number') return;
      window.clearTimeout(filterTimer);
      filterTimer = window.setTimeout(() => form.requestSubmit?.() ?? form.submit(), 250);
    });
  }
  document.addEventListener('click', event => {
    const button = event.target.closest('[data-ay-qty-minus], [data-ay-qty-plus]');
    if (!button) return;
    const wrap = button.closest('[data-ay-qty]');
    const input = wrap?.querySelector('input');
    if (!input) return;
    const min = Number(input.min || 0), max = input.max ? Number(input.max) : Infinity;
    const next = Number(input.value || 0) + (button.hasAttribute('data-ay-qty-plus') ? 1 : -1) * Number(input.step || 1);
    input.value = Math.min(max, Math.max(min, next));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ------------------------------------------------------------------ */
  /* 7. Confort de lecture (mode léger / mouvement réduit)               */
  /* ------------------------------------------------------------------ */
  (() => {
    const connection = navigator.connection;
    const key = 'ay-browsing-mode';
    let preference = 'auto';
    let battery = null;
    let previous = '';
    const read = () => {
      try { const value = localStorage.getItem(key); preference = ['auto', 'light', 'standard'].includes(value) ? value : 'auto'; }
      catch (_) { preference = 'auto'; }
    };
    read();
    function apply() {
      const lowBattery = battery && !battery.charging && battery.level <= 0.2;
      const fallback = preference === 'auto' && !battery && !connection;
      const light = preference === 'light' || (preference === 'auto' && Boolean(lowBattery || connection?.saveData));
      const reducedMotion = light || reduced.matches;
      root.dataset.ayLight = String(light);
      root.dataset.ayReduced = String(reducedMotion);
      document.querySelectorAll('[data-ay-energy-mode]').forEach(select => { select.value = preference; });
      document.querySelectorAll('[data-ay-energy-status]').forEach(status => {
        const text = light ? status.dataset.light : fallback ? status.dataset.fallback : status.dataset.standard;
        if (status.textContent !== text) status.textContent = text;
      });
      if (reducedMotion) document.querySelectorAll('video,audio').forEach(media => { if (!media.paused) media.pause(); });
      if (reducedMotion) document.querySelectorAll('main .shopify-section').forEach(section => section.classList.add('is-in'));
      const signature = `${light}:${reducedMotion}`;
      if (signature !== previous) { previous = signature; document.dispatchEvent(new CustomEvent('ay:comfortchange', { detail: { light, reduced: reducedMotion } })); }
    }
    function prepareMedia(scope) {
      const media = scope.matches?.('video,audio') ? [scope] : [...scope.querySelectorAll('video,audio')];
      media.forEach(item => { if (item.autoplay) { item.autoplay = false; item.removeAttribute('autoplay'); item.pause(); } item.controls = true; item.preload = 'none'; });
    }
    function init() {
      document.querySelectorAll('[data-ay-energy-mode]').forEach(select => {
        if (select.dataset.ready) return;
        select.dataset.ready = 'true';
        select.addEventListener('change', () => { preference = select.value; try { localStorage.setItem(key, preference); } catch (_) {} apply(); });
      });
      document.querySelectorAll('[data-ay-comfort]').forEach(panel => {
        if (panel.dataset.ready) return;
        panel.dataset.ready = 'true';
        panel.addEventListener('keydown', event => { if (event.key === 'Escape') { panel.open = false; panel.querySelector('summary').focus(); } });
      });
      prepareMedia(document);
      apply();
    }
    reduced.addEventListener('change', apply);
    connection?.addEventListener('change', apply);
    window.addEventListener('storage', event => { if (event.key === key) { read(); apply(); } });
    document.addEventListener('shopify:section:load', init);
    document.addEventListener('visibilitychange', () => { if (document.hidden) document.querySelectorAll('video,audio').forEach(media => media.pause()); });
    init();
    if (typeof navigator.getBattery === 'function') {
      navigator.getBattery().then(result => { battery = result; battery.addEventListener('levelchange', apply); battery.addEventListener('chargingchange', apply); apply(); }).catch(() => {});
    }
  })();

  window.ayCart = { open: openDrawer, refresh: refreshCart };
  /* Bulle « Le mot de Lamyaa » sur les pages où ZEM est annoncé (fermable, réouvrable, mémorisée pour la visite) */
  (() => {
    const raw = location.pathname;
    const p = raw.startsWith('/en/') ? raw.slice(3) : (raw === '/en' ? '/' : raw);
    const wanted = p === '/' || p.startsWith('/products/zem-') || p.startsWith('/collections/zem-') || p.startsWith('/pages/concours-zem');
    if (!wanted || document.getElementById('AYZemWord')) return;
    const isEn = document.documentElement.lang === 'en';
    const base = isEn ? '/en' : '';
    let closed = false;
    try { closed = sessionStorage.getItem('ay-zem-word') === '1'; } catch (_) {}
    const box = document.createElement('div');
    box.id = 'AYZemWord';
    box.className = 'ay-zem-word' + (closed ? ' is-closed' : '');
    box.innerHTML = '<button type="button" class="ay-zem-word__tab" aria-label="' + (isEn ? 'Open: a word from Lamyaa' : 'Ouvrir : le mot de Lamyaa') + '">♥</button><div class="ay-zem-word__card" role="dialog" aria-label="' + (isEn ? 'A word from Lamyaa' : 'Le mot de Lamyaa') + '"><button type="button" class="ay-zem-word__close" aria-label="' + (isEn ? 'Close' : 'Fermer') + '">×</button><p class="ay-zem-word__kicker">♥ ' + (isEn ? 'A word from Lamyaa, founder' : 'Le mot de Lamyaa, fondatrice') + '</p><p class="ay-zem-word__text">' + (isEn ? 'I did not invent adaptive clothing. But since the birth of my first son, Adam, I have been looking for it, and I am still looking today. ZEM Adaptive was born from my nights, my hospitals, my two boys.' : 'Je n’ai pas inventé les vêtements adaptés. Mais depuis la naissance de mon premier fils, Adam, je les ai cherchés, et je les cherche encore aujourd’hui. ZEM Adaptée est née de mes nuits, de mes hôpitaux, de mes deux garçons.') + '</p><a class="ay-zem-word__link" href="' + base + '/pages/zem#pourquoi-zem">' + (isEn ? 'Read why ZEM exists →' : 'Lire pourquoi ZEM existe →') + '</a></div>';
    document.body.append(box);
    const setClosed = v => { box.classList.toggle('is-closed', v); try { sessionStorage.setItem('ay-zem-word', v ? '1' : '0'); } catch (_) {} };
    box.querySelector('.ay-zem-word__close').addEventListener('click', () => setClosed(true));
    box.querySelector('.ay-zem-word__tab').addEventListener('click', () => setClosed(false));
  })();
})();


(() => {
  let pending = false;
  const update = () => {
    pending = false;
    const open = document.querySelector('#AYMenu[data-open="true"], dialog[open]');
    const modal = [...document.querySelectorAll('[aria-modal="true"]')].some(node => {
      const rect = node.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0 && getComputedStyle(node).visibility !== 'hidden';
    });
    const value = String(Boolean(open || modal));
    if (document.documentElement.dataset.ayOverlay !== value) document.documentElement.dataset.ayOverlay = value;
  };
  const schedule = () => {
    if (pending) return;
    pending = true;
    requestAnimationFrame(update);
  };
  new MutationObserver(schedule).observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['open', 'data-open', 'hidden', 'aria-hidden', 'aria-modal', 'style', 'class'] });
  document.addEventListener('shopify:section:load', schedule);
  update();
})();
/* A&Y — suivi : éviter le double « ajout au panier » (Shopify compte l'envoi du formulaire ET l'appel AJAX). L'attribut action n'est retiré que si ce script est chargé (repli sans JS conservé). */ (()=>{const strip=root=>root.querySelectorAll('form[data-ay-ajax-cart][action]').forEach(f=>{f.dataset.ayAction=f.getAttribute('action');f.removeAttribute('action');});strip(document);document.addEventListener('shopify:section:load',e=>strip(e.target));new MutationObserver(m=>m.forEach(r=>r.addedNodes.forEach(n=>{if(n.nodeType===1)strip(n.matches&&n.matches('form')?n.parentNode||document:n)}))).observe(document.documentElement,{childList:true,subtree:true});document.addEventListener('submit',e=>{const f=e.target.closest&&e.target.closest('form[data-ay-ajax-cart]');if(f&&!f.getAttribute('action')&&f.dataset.ayBusy==='true')e.preventDefault();},true);})();